import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Skeleton,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { GlobalContext } from 'providers/Global';
import { ModalType, SCREEN_MD } from '../../utils/constants';
import { useToast } from 'hooks/useToast';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from '../../api/notificationAPI';

interface Preferences {
  enableSignatureNeeded: boolean;
  enableTransactionCompleted: boolean;
  enableWalletConfigUpdated: boolean;
  enableWalletInvitation: boolean;
}

const defaultPrefs: Preferences = {
  enableSignatureNeeded: true,
  enableTransactionCompleted: true,
  enableWalletConfigUpdated: true,
  enableWalletInvitation: true,
};

const preferenceItems: { key: keyof Preferences; label: string; description: string }[] = [
  {
    key: 'enableSignatureNeeded',
    label: 'Signature Needed',
    description: 'When a transaction requires your signature',
  },
  {
    key: 'enableTransactionCompleted',
    label: 'Transaction Completed',
    description: 'When a transaction has been executed',
  },
  {
    key: 'enableWalletConfigUpdated',
    label: 'Wallet Config Updated',
    description: 'When wallet signers or threshold change',
  },
  {
    key: 'enableWalletInvitation',
    label: 'Wallet Invitation',
    description: 'When you are invited to join a wallet',
  },
];

const NotificationSettingsDrawer = () => {
  const {
    connection: { accountId },
    modals: {
      [`${ModalType.NotificationSettingsDrawer}`]: { open },
    },
    closeModal,
  } = useContext(GlobalContext);

  const isMobile = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });
  const { showToast } = useToast();
  const [prefs, setPrefs] = useState<Preferences>(defaultPrefs);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPreferences = useCallback(async () => {
    if (!accountId) return;
    setIsLoading(true);
    try {
      const res = await getNotificationPreferences();
      if (res?.data) {
        setPrefs({
          enableSignatureNeeded: res.data.enableSignatureNeeded ?? true,
          enableTransactionCompleted: res.data.enableTransactionCompleted ?? true,
          enableWalletConfigUpdated: res.data.enableWalletConfigUpdated ?? true,
          enableWalletInvitation: res.data.enableWalletInvitation ?? true,
        });
      }
    } catch (error: unknown) {
      console.error('Failed to fetch notification preferences:', error);
    } finally {
      setIsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (open) {
      fetchPreferences();
    }
  }, [open, fetchPreferences]);

  const handleToggle = async (key: keyof Preferences) => {
    const newValue = !prefs[key];
    // Optimistic update
    setPrefs((prev) => ({ ...prev, [key]: newValue }));
    try {
      await updateNotificationPreferences({ [key]: newValue });
    } catch (error: unknown) {
      // Revert on failure
      setPrefs((prev) => ({ ...prev, [key]: !newValue }));
      console.error('Failed to update notification preference:', error);
      showToast('Failed to update setting', 'error', undefined, 'top');
    }
  };

  return (
    <Drawer
      isOpen={open}
      onClose={() => closeModal(ModalType.NotificationSettingsDrawer)}
      placement="right"
      size={isMobile ? 'full' : 'md'}
    >
      <DrawerOverlay backdropFilter="blur(10px)" />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <Text variant="bodyBold" fontSize="xl">
            Notification Settings
          </Text>
        </DrawerHeader>
        <DrawerBody py={6}>
          <Text fontSize="sm" color="blackAlpha.600" mb={6}>
            Choose which notifications you want to receive.
          </Text>
          <Flex direction="column" gap={1}>
            {preferenceItems.map((item) => (
              <Flex
                key={item.key}
                justifyContent="space-between"
                alignItems="center"
                py={4}
                px={2}
                borderRadius={8}
                _hover={{ bg: 'blackAlpha.50' }}
              >
                <Box flex={1} mr={4}>
                  <Text size="sm" variant="bodyBold">
                    {item.label}
                  </Text>
                  <Text fontSize="xs" color="blackAlpha.500" mt={0.5}>
                    {item.description}
                  </Text>
                </Box>
                {isLoading ? (
                  <Skeleton w="34px" h="20px" borderRadius="full" />
                ) : (
                  <Switch
                    colorScheme="green"
                    isChecked={prefs[item.key]}
                    onChange={() => handleToggle(item.key)}
                    aria-label={`Toggle ${item.label}`}
                  />
                )}
              </Flex>
            ))}
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default NotificationSettingsDrawer;
